import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { formatPrice, formatDate, getStatusColor } from '../../lib/utils';
import { Package, User, CreditCard, MapPin, Truck } from 'lucide-react';

export default function OrderViewDialog({ open, onClose, order }) {
  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Header */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                Order #{order.id?.slice(-8)}
              </h3>
              <p className="text-gray-600 mt-1">{formatDate(order.created_at)}</p>
            </div>
            <Badge className={`${getStatusColor(order.status)} capitalize text-sm px-3 py-1`}>
              {order.status}
            </Badge>
          </div>

          {/* Customer Information */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-400" />
              <h4 className="font-semibold text-gray-900">Customer Information</h4>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">User ID: {order.user_id}</p>
            </div>
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-gray-400" />
                <h4 className="font-semibold text-gray-900">Shipping Address</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-900">{order.shipping_address.street}</p>
                <p className="text-sm text-gray-900">
                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                </p>
                <p className="text-sm text-gray-900">{order.shipping_address.country}</p>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Package className="w-5 h-5 text-gray-400" />
              <h4 className="font-semibold text-gray-900">Order Items</h4>
            </div>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-4">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <h5 className="font-semibold text-gray-900">{item.name}</h5>
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                      <p className="text-sm text-gray-600">Price: {formatPrice(item.price)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Information */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <CreditCard className="w-5 h-5 text-gray-400" />
              <h4 className="font-semibold text-gray-900">Payment Information</h4>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Payment Status:</span>
                <Badge className={`${getStatusColor(order.payment_status)} capitalize text-xs`}>
                  {order.payment_status}
                </Badge>
              </div>
              {order.payment_method && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Method:</span>
                  <span className="text-sm text-gray-900 capitalize">{order.payment_method}</span>
                </div>
              )}
              {order.stripe_session_id && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Transaction ID:</span>
                  <code className="text-xs bg-white px-2 py-1 rounded">
                    {order.stripe_session_id}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Tracking Information */}
          {order.tracking_number && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Truck className="w-5 h-5 text-gray-400" />
                <h4 className="font-semibold text-gray-900">Tracking Information</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <code className="text-sm bg-white px-3 py-2 rounded">
                  {order.tracking_number}
                </code>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="pt-4 border-t">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="text-gray-900">{formatPrice(order.subtotal || order.total)}</span>
              </div>
              {order.shipping_cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping:</span>
                  <span className="text-gray-900">{formatPrice(order.shipping_cost)}</span>
                </div>
              )}
              {order.tax > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax:</span>
                  <span className="text-gray-900">{formatPrice(order.tax)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-bold text-gray-900">Total:</span>
                <span className="font-bold text-gray-900 text-xl">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
